/**
 * ============================================================================
 * Iron Meridian - localStorage to SQLite Migration
 * ============================================================================
 * 
 * PURPOSE:
 * Migrate all game data from localStorage to SQLite (Cloudflare D1).
 * Migration is idempotent and can be run multiple times safely.
 * 
 * PROCESS:
 * 1. Check for legacy localStorage saves
 * 2. Deserialize and validate data
 * 3. Populate SQLite tables via persistence adapter
 * 4. Verify integrity
 * 5. Delete legacy localStorage keys (only after verification)
 * 6. Log migration result
 * 
 * ============================================================================
 */

import db from './db.js'

// ============================================================================
// LEGACY STORAGE KEYS
// ============================================================================

const LEGACY_KEYS = {
  // Main game save (v1 and v2)
  GAME_SAVE_V1: 'ironmeridian.singleplayerSave.v1',
  GAME_SAVE_V2: 'ironmeridian.singleplayerSave.v2',
  
  // Tech state
  TECH_STATE: 'ironMeridian_techState',
  
  // Diplomacy state
  DIPLOMACY_STATE: 'ironMeridian_diplomacyState',
  
  // Tutorial completion
  TUTORIAL_COMPLETE: 'ironmeridian.tutorialComplete.v1',
  
  // Settings (various)
  SETTINGS_PREFIX: 'ironmeridian.setting.'
}

// Migration log key to track completed migrations
const MIGRATION_STATUS_KEY = 'ironmeridian.migration.status'

// ============================================================================
// MIGRATION STATUS
// ============================================================================

/**
 * @typedef {Object} MigrationStatus
 * @property {boolean} completed - Whether migration has been completed
 * @property {string} [completedAt] - ISO timestamp of completion
 * @property {number} [recordsMigrated] - Number of records migrated
 * @property {string[]} [migratedKeys] - Keys that were migrated
 * @property {string[]} [errors] - Any errors encountered
 */

/**
 * Get migration status
 * @returns {MigrationStatus}
 */
export function getMigrationStatus() {
  if (typeof window === 'undefined') {
    return { completed: false }
  }
  
  try {
    const stored = window.localStorage.getItem(MIGRATION_STATUS_KEY)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch {
    // Ignore errors
  }
  
  return { completed: false }
}

/**
 * Save migration status
 * @param {MigrationStatus} status
 */
function saveMigrationStatus(status) {
  if (typeof window === 'undefined') return
  
  try {
    window.localStorage.setItem(MIGRATION_STATUS_KEY, JSON.stringify(status))
  } catch {
    // Ignore errors
  }
}

// ============================================================================
// LEGACY DATA LOADING
// ============================================================================

/**
 * Load legacy game save from localStorage
 * @returns {Object|null}
 */
function loadLegacyGameSave() {
  if (typeof window === 'undefined') return null
  
  // Try v2 first, then v1
  for (const key of [LEGACY_KEYS.GAME_SAVE_V2, LEGACY_KEYS.GAME_SAVE_V1]) {
    try {
      const raw = window.localStorage.getItem(key)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (parsed && parsed.gameState && parsed.countries) {
          return { key, data: parsed }
        }
      }
    } catch {
      // Continue to next key
    }
  }
  
  return null
}

/**
 * Load legacy tech state from localStorage
 * @returns {Object|null}
 */
function loadLegacyTechState() {
  if (typeof window === 'undefined') return null
  
  try {
    const raw = window.localStorage.getItem(LEGACY_KEYS.TECH_STATE)
    if (raw) {
      return JSON.parse(raw)
    }
  } catch {
    // Ignore errors
  }
  
  return null
}

/**
 * Load legacy diplomacy state from localStorage
 * @returns {Object|null}
 */
function loadLegacyDiplomacyState() {
  if (typeof window === 'undefined') return null
  
  try {
    const raw = window.localStorage.getItem(LEGACY_KEYS.DIPLOMACY_STATE)
    if (raw) {
      return JSON.parse(raw)
    }
  } catch {
    // Ignore errors
  }
  
  return null
}

/**
 * Load legacy tutorial state
 * @returns {boolean}
 */
function loadLegacyTutorialState() {
  if (typeof window === 'undefined') return false
  
  try {
    return window.localStorage.getItem(LEGACY_KEYS.TUTORIAL_COMPLETE) === 'true'
  } catch {
    return false
  }
}

/**
 * Load all legacy settings
 * @returns {Object}
 */
function loadLegacySettings() {
  if (typeof window === 'undefined') return {}
  
  const settings = {}
  
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i)
      if (key && key.startsWith(LEGACY_KEYS.SETTINGS_PREFIX)) {
        const settingKey = key.substring(LEGACY_KEYS.SETTINGS_PREFIX.length)
        try {
          settings[settingKey] = JSON.parse(window.localStorage.getItem(key))
        } catch {
          settings[settingKey] = window.localStorage.getItem(key)
        }
      }
    }
  } catch {
    // Ignore errors
  }
  
  return settings
}

// ============================================================================
// DATA VALIDATION
// ============================================================================

/**
 * Validate game save data structure
 * @param {Object} data
 * @returns {{valid: boolean, errors: string[]}}
 */
function validateGameSave(data) {
  const errors = []
  
  if (!data.gameState) {
    errors.push('Missing gameState')
  } else {
    if (!data.gameState.playerCountry) errors.push('Missing playerCountry')
    if (data.gameState.currentTurn === undefined) errors.push('Missing currentTurn')
  }
  
  if (!data.countries || typeof data.countries !== 'object') {
    errors.push('Missing or invalid countries')
  }
  
  return { valid: errors.length === 0, errors }
}

/**
 * Validate tech state data structure
 * @param {Object} data
 * @returns {{valid: boolean, errors: string[]}}
 */
function validateTechState(data) {
  const errors = []
  
  if (!Array.isArray(data.researched)) {
    errors.push('Missing or invalid researched array')
  }
  
  if (data.researchPoints === undefined) {
    errors.push('Missing researchPoints')
  }
  
  return { valid: errors.length === 0, errors }
}

/**
 * Validate diplomacy state data structure
 * @param {Object} data
 * @returns {{valid: boolean, errors: string[]}}
 */
function validateDiplomacyState(data) {
  const errors = []
  
  if (!data.relationships || typeof data.relationships !== 'object') {
    errors.push('Missing or invalid relationships')
  }
  
  return { valid: errors.length === 0, errors }
}

// ============================================================================
// MIGRATION CORE
// ============================================================================

/**
 * Migrate localStorage data to SQLite
 * @param {Object} [options]
 * @param {boolean} [options.force=false] - Force migration even if already completed
 * @param {boolean} [options.keepLegacy=false] - Keep legacy localStorage keys after migration
 * @returns {Promise<MigrationStatus>}
 */
export async function migrateToSQLite(options = {}) {
  const { force = false, keepLegacy = false } = options
  
  // Check if already migrated
  const existingStatus = getMigrationStatus()
  if (existingStatus.completed && !force) {
    console.log('[Migration] Already completed, skipping')
    return existingStatus
  }
  
  console.log('[Migration] Starting localStorage → SQLite migration')
  
  const status = {
    completed: false,
    startedAt: new Date().toISOString(),
    recordsMigrated: 0,
    migratedKeys: [],
    errors: []
  }
  
  // Check database availability
  const dbStatus = await db.checkDatabase()
  if (!dbStatus.available) {
    console.warn('[Migration] Database not available, skipping migration')
    status.errors.push('Database not available')
    saveMigrationStatus(status)
    return status
  }
  
  // Initialize database schema if needed
  if (!dbStatus.initialized) {
    console.log('[Migration] Initializing database schema')
    const initResult = await db.initializeDatabase()
    if (!initResult.success) {
      status.errors.push(`Schema initialization failed: ${initResult.error}`)
      saveMigrationStatus(status)
      return status
    }
  }
  
  // Load legacy data
  const legacyGame = loadLegacyGameSave()
  const legacyTech = loadLegacyTechState()
  const legacyDiplomacy = loadLegacyDiplomacyState()
  const legacyTutorial = loadLegacyTutorialState()
  const legacySettings = loadLegacySettings()
  
  // Check if there's anything to migrate
  if (!legacyGame && !legacyTech && !legacyDiplomacy && !legacyTutorial) {
    console.log('[Migration] No legacy data found')
    status.completed = true
    status.completedAt = new Date().toISOString()
    status.message = 'No legacy data to migrate'
    saveMigrationStatus(status)
    return status
  }
  
  // Migrate game save
  if (legacyGame) {
    console.log('[Migration] Migrating game save from', legacyGame.key)
    
    const validation = validateGameSave(legacyGame.data)
    if (!validation.valid) {
      console.warn('[Migration] Invalid game save:', validation.errors)
      status.errors.push(...validation.errors.map(e => `GameSave: ${e}`))
    } else {
      // Merge in tech and diplomacy if available
      const saveData = {
        gameState: legacyGame.data.gameState,
        countries: legacyGame.data.countries,
        actionQueue: legacyGame.data.actionQueue || [],
        combatLog: legacyGame.data.combatLog || []
      }
      
      // Include tech state
      if (legacyTech) {
        const techValidation = validateTechState(legacyTech)
        if (techValidation.valid) {
          saveData.techState = legacyTech
        } else {
          status.errors.push(...techValidation.errors.map(e => `TechState: ${e}`))
        }
      }
      
      // Include diplomacy state
      if (legacyDiplomacy) {
        const dipValidation = validateDiplomacyState(legacyDiplomacy)
        if (dipValidation.valid) {
          saveData.diplomacy = legacyDiplomacy
        } else {
          status.errors.push(...dipValidation.errors.map(e => `Diplomacy: ${e}`))
        }
      }
      
      // Save to SQLite
      const saveResult = await db.saveGameState(saveData)
      
      if (saveResult.success) {
        console.log('[Migration] Game save migrated successfully')
        status.recordsMigrated++
        status.migratedKeys.push(legacyGame.key)
        
        if (legacyTech) {
          status.recordsMigrated++
          status.migratedKeys.push(LEGACY_KEYS.TECH_STATE)
        }
        
        if (legacyDiplomacy) {
          status.recordsMigrated++
          status.migratedKeys.push(LEGACY_KEYS.DIPLOMACY_STATE)
        }
      } else {
        status.errors.push(`Failed to save game: ${saveResult.error}`)
      }
    }
  }
  
  // Migrate tutorial state
  if (legacyTutorial) {
    console.log('[Migration] Migrating tutorial state')
    await db.markTutorialComplete('main')
    status.recordsMigrated++
    status.migratedKeys.push(LEGACY_KEYS.TUTORIAL_COMPLETE)
  }
  
  // Migrate settings
  for (const [key, value] of Object.entries(legacySettings)) {
    await db.saveSetting(key, value)
    status.recordsMigrated++
    status.migratedKeys.push(`${LEGACY_KEYS.SETTINGS_PREFIX}${key}`)
  }
  
  // Verify migration
  console.log('[Migration] Verifying migration')
  const verifyResult = await db.loadGameState()
  
  if (verifyResult.success && verifyResult.data) {
    console.log('[Migration] Verification successful')
    
    // Clean up legacy keys if requested
    if (!keepLegacy) {
      console.log('[Migration] Cleaning up legacy localStorage keys')
      cleanupLegacyKeys(status.migratedKeys)
    }
    
    status.completed = true
    status.completedAt = new Date().toISOString()
  } else {
    status.errors.push('Migration verification failed')
  }
  
  saveMigrationStatus(status)
  console.log('[Migration] Migration complete:', status)
  
  return status
}

/**
 * Clean up legacy localStorage keys
 * @param {string[]} keys - Keys to remove
 */
function cleanupLegacyKeys(keys) {
  if (typeof window === 'undefined') return
  
  for (const key of keys) {
    try {
      window.localStorage.removeItem(key)
    } catch {
      // Ignore errors
    }
  }
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Check if migration is needed
 * @returns {boolean}
 */
export function isMigrationNeeded() {
  const status = getMigrationStatus()
  if (status.completed) return false
  
  // Check if there's any legacy data
  const hasLegacyGame = loadLegacyGameSave() !== null
  const hasLegacyTech = loadLegacyTechState() !== null
  const hasLegacyDiplomacy = loadLegacyDiplomacyState() !== null
  const hasLegacyTutorial = loadLegacyTutorialState()
  
  return hasLegacyGame || hasLegacyTech || hasLegacyDiplomacy || hasLegacyTutorial
}

/**
 * Reset migration status (for testing)
 */
export function resetMigrationStatus() {
  if (typeof window === 'undefined') return
  
  try {
    window.localStorage.removeItem(MIGRATION_STATUS_KEY)
  } catch {
    // Ignore errors
  }
}

/**
 * Get list of legacy keys that exist in localStorage
 * @returns {string[]}
 */
export function getLegacyKeys() {
  if (typeof window === 'undefined') return []
  
  const keys = []
  
  try {
    for (const key of Object.values(LEGACY_KEYS)) {
      if (typeof key === 'string' && !key.endsWith('.')) {
        if (window.localStorage.getItem(key) !== null) {
          keys.push(key)
        }
      }
    }
    
    // Check for settings with prefix
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i)
      if (key && key.startsWith(LEGACY_KEYS.SETTINGS_PREFIX)) {
        keys.push(key)
      }
    }
  } catch {
    // Ignore errors
  }
  
  return keys
}

// ============================================================================
// AUTO-MIGRATION HOOK
// ============================================================================

/**
 * Run migration on app load (call from App.jsx or main.jsx)
 * @returns {Promise<MigrationStatus>}
 */
export async function runMigrationOnLoad() {
  if (!isMigrationNeeded()) {
    console.log('[Migration] No migration needed')
    return getMigrationStatus()
  }
  
  console.log('[Migration] Legacy data detected, starting migration')
  return migrateToSQLite()
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  migrateToSQLite,
  isMigrationNeeded,
  getMigrationStatus,
  resetMigrationStatus,
  getLegacyKeys,
  runMigrationOnLoad
}
